import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button, cn } from './ui';

const VARIANTS = {
  warning: {
    icon: 'bg-amber-50 text-amber-700',
    button: 'bg-amber-700 hover:bg-amber-800 focus:ring-amber-500/20',
  },
  danger: {
    icon: 'bg-red-50 text-red-700',
    button: 'bg-red-700 hover:bg-red-800 focus:ring-red-500/20',
  },
  success: {
    icon: 'bg-giggs-green/10 text-giggs-green',
    button: 'bg-giggs-green hover:bg-giggs-green-hover focus:ring-giggs-green/20',
  },
};

const ConfirmationModal = ({
  open,
  title = 'Confirm action',
  message = 'Please confirm before continuing. This action may not be reversible.',
  confirmText = 'Confirm',
  cancelText = 'Go back',
  variant = 'warning',
  onConfirm,
  onCancel,
}) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onCancel?.();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onCancel]);

  if (!open || typeof document === 'undefined') return null;
  const palette = VARIANTS[variant] || VARIANTS.warning;

  return createPortal(
    <div
      data-confirmation-modal="true"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onCancel?.();
      }}
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-[#0A1811]/50 p-5 backdrop-blur-[2px]"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirmation-modal-title"
        aria-describedby="confirmation-modal-message"
        className="w-full max-w-[430px] rounded-[18px] bg-white p-6 shadow-[0_24px_70px_rgba(10,24,17,0.22)] sm:p-[26px]"
      >
        <div aria-hidden="true" className={cn('mb-4 flex h-[52px] w-[52px] items-center justify-center rounded-full', palette.icon)}>
          <svg width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path d="M12 9v4" /><path d="M12 17h.01" /><path d="M10.3 3.7 2.4 17.4A2 2 0 0 0 4.1 20h15.8a2 2 0 0 0 1.7-2.6L13.7 3.7a2 2 0 0 0-3.4 0Z" />
          </svg>
        </div>
        <h3 id="confirmation-modal-title" className="m-0 font-nunito text-xl font-extrabold leading-[1.3] text-[#10271d]">{title}</h3>
        <p id="confirmation-modal-message" className="mb-[22px] mt-2.5 font-dm text-sm leading-[1.6] text-[#6B7F75]">{message}</p>
        <div className="flex flex-col-reverse gap-2.5 sm:flex-row sm:justify-end">
          <Button data-confirmation-control="true" variant="outline" onClick={onCancel}>{cancelText}</Button>
          <Button data-confirmation-control="true" className={palette.button} onClick={onConfirm} autoFocus>{confirmText}</Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ConfirmationModal;
